// src/components/System/SenderIds.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useModal } from '../../context/ModalContext';
import { shouldShowApiToast } from '../../utils/helpers';
import {
  getSenderIds,
  getSenderId,
  createSenderId,
  updateSenderId,
  deleteSenderId,
  approveSenderId,
  rejectSenderId,
  toggleSenderIdStatus,
  setDefaultSenderId,
  getDefaultSenderId,
  clearApiMessage,
  clearError,
  clearSenderIdState,
  clearCurrentSenderId,
  selectSenderIds,
  selectSenderIdPagination,
  selectCurrentSenderId,
  selectDefaultSenderId,
  selectSenderIdLoading,
  selectSenderIdSubmitting,
  selectSenderIdApiMessage,
  selectSenderIdError,
  selectSenderIdSuccess,
} from '../../store/slices/system/senderIdSlice';
import {
  Pencil,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Search,
  Loader2,
  Plus,
  Globe,
  Star,
  StarOff,
  ToggleLeft,
  ToggleRight,
  Send,
  LayoutGrid,
  List,
  ClipboardList,
  Save,
  Clock,
} from 'lucide-react';

// ==================== COUNTRY OPTIONS ====================
const COUNTRY_OPTIONS = [
  { value: '', label: 'Select Country' },
  { value: 'GH', label: 'Ghana' },
//   { value: 'NG', label: 'Nigeria' },
//   { value: 'KE', label: 'Kenya' },
//   { value: 'ZA', label: 'South Africa' },
//   { value: 'TZ', label: 'Tanzania' },
//   { value: 'UG', label: 'Uganda' },
//   { value: 'RW', label: 'Rwanda' },
//   { value: 'ZM', label: 'Zambia' },
//   { value: 'ZW', label: 'Zimbabwe' },
//   { value: 'MW', label: 'Malawi' },
//   { value: 'SL', label: 'Sierra Leone' },
//   { value: 'LR', label: 'Liberia' },
//   { value: 'CM', label: 'Cameroon' },
//   { value: 'CI', label: "Côte d'Ivoire" },
//   { value: 'SN', label: 'Senegal' },
//   { value: 'ML', label: 'Mali' },
//   { value: 'BF', label: 'Burkina Faso' },
//   { value: 'BJ', label: 'Benin' },
//   { value: 'TG', label: 'Togo' },
//   { value: 'US', label: 'United States' },
//   { value: 'GB', label: 'United Kingdom' },
//   { value: 'CA', label: 'Canada' },
//   { value: 'AU', label: 'Australia' },
//   { value: 'IN', label: 'India' },
];

const SenderIds = ({ showToast }) => {
  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();

  // ==================== STATE ====================
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState('grid');

  // ==================== REFS ====================
  const nameRef = useRef(null);
  const descriptionRef = useRef(null);
  const typeRef = useRef(null);
  const countryCodeRef = useRef(null);
  const statusRef = useRef(null);
  const isDefaultRef = useRef(null);
  const editingIdRef = useRef(null);

  // Track last toast message to prevent duplicates
  const lastToastRef = useRef({ message: null, timestamp: 0 });

  // ==================== SELECTORS ====================
  const senderIds = useSelector(selectSenderIds) || [];
  const pagination = useSelector(selectSenderIdPagination) || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0,
  };
  const currentSenderId = useSelector(selectCurrentSenderId);
  const defaultSenderId = useSelector(selectDefaultSenderId);
  const loading = useSelector(selectSenderIdLoading);
  const isSubmittingRedux = useSelector(selectSenderIdSubmitting);
  const apiMessage = useSelector(selectSenderIdApiMessage);
  const error = useSelector(selectSenderIdError);
  const success = useSelector(selectSenderIdSuccess);

  // ==================== EFFECTS ====================

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([
          dispatch(getSenderIds({ per_page: itemsPerPage, page: currentPage })),
          dispatch(getDefaultSenderId()),
        ]);
      } catch (err) {
        console.error('Error loading sender IDs:', err);
      }
    };
    loadData();

    return () => {
      dispatch(clearSenderIdState());
    };
  }, [dispatch]);

  // Handle search and filters
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = {
        search: searchTerm,
        per_page: itemsPerPage,
        page: currentPage,
      };
      if (statusFilter) params.status = statusFilter;

      dispatch(getSenderIds(params));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter, currentPage, itemsPerPage, dispatch]);

  // Display API messages - with deduplication
  useEffect(() => {
    const msg = apiMessage || (error ? error : null);
    if (!msg || !showToast || !shouldShowApiToast(msg, !!error)) return;

    const messageKey = `${msg}_${error ? 'error' : 'success'}`;
    const now = Date.now();

    if (lastToastRef.current.message === messageKey &&
        (now - lastToastRef.current.timestamp) < 2000) {
      return;
    }

    lastToastRef.current = { message: messageKey, timestamp: now };

    const toastType = error ? 'error' : 'success';
    showToast(msg, toastType);

    setTimeout(() => {
      dispatch(clearApiMessage());
      dispatch(clearError());
    }, 500);
  }, [apiMessage, error, dispatch, showToast]);

  // ==================== TOAST HELPER ====================
  const showToastMessage = useCallback((msg, type = 'info') => {
    if (showToast && typeof showToast === 'function') {
      showToast(msg, type);
    }
  }, [showToast]);

  // ==================== PAGINATION ====================

  const totalItems = pagination.total || 0;
  const totalPages = pagination.last_page || 1;
  const currentPageNum = pagination.current_page || 1;
  const perPage = pagination.per_page || 15;

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPageNum > 1) {
      setCurrentPage(currentPageNum - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToNextPage = () => {
    if (currentPageNum < totalPages) {
      setCurrentPage(currentPageNum + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPageNum - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  // ==================== HELPERS ====================

  const getStatusBadge = (status) => {
    const map = {
      pending: { class: 'badge-amber', label: 'Pending' },
      approved: { class: 'badge-green', label: 'Approved' },
      rejected: { class: 'badge-red', label: 'Rejected' },
      active: { class: 'badge-green', label: 'Active' },
      inactive: { class: 'badge-navy', label: 'Inactive' },
    };
    const s = map[status] || map.inactive;
    return <span className={`badge ${s.class}`}>{s.label}</span>;
  };

  const getTypeLabel = (type) => {
    return type === 'alphanumeric' ? 'Alphanumeric' : 'Numeric';
  };

  const isActive = (item) => {
    return item.status === 'approved' || item.status === 'active';
  };

  const validateForm = (values) => {
    const errors = {};
    if (!values.name) {
      errors.name = 'Sender ID is required';
    } else if (values.name.length < 2) {
      errors.name = 'Sender ID must be at least 2 characters';
    } else if (values.name.length > 11) {
      errors.name = 'Sender ID must be 11 characters or less';
    }
    if (values.type && !['alphanumeric', 'numeric'].includes(values.type)) {
      errors.type = 'Invalid type';
    }
    return errors;
  };

  // ==================== FORM HELPERS ====================
  const getFormValues = () => {
    return {
      name: nameRef.current?.value?.trim() || '',
      description: descriptionRef.current?.value?.trim() || '',
      type: typeRef.current?.value || 'alphanumeric',
      country_code: countryCodeRef.current?.value || null,
      status: statusRef.current?.value || 'pending',
      is_default: isDefaultRef.current?.checked || false,
    };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // ==================== CRUD OPERATIONS ====================

  const openCreateModal = () => {
    editingIdRef.current = null;

    setTimeout(() => {
      if (nameRef.current) nameRef.current.value = '';
      if (descriptionRef.current) descriptionRef.current.value = '';
      if (typeRef.current) typeRef.current.value = 'alphanumeric';
      if (countryCodeRef.current) countryCodeRef.current.value = '';
      if (statusRef.current) statusRef.current.value = 'pending';
      if (isDefaultRef.current) isDefaultRef.current.checked = false;
    }, 0);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Plus size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Create New Sender ID</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Sender ID <span style={{ color: 'var(--red)' }}>*</span></label>
            <input className="form-input" placeholder="e.g. GraceChapel" ref={nameRef} maxLength="11" />
            <div style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '4px' }}>
              Max 11 characters, alphanumeric only
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-textarea" rows="2" placeholder="Brief description" ref={descriptionRef} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Type</label>
              <select className="form-select" ref={typeRef} defaultValue="alphanumeric">
                <option value="alphanumeric">Alphanumeric</option>
                <option value="numeric">Numeric</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Country Code</label>
              <select className="form-select" ref={countryCodeRef} defaultValue="">
                {COUNTRY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-select" ref={statusRef} defaultValue="pending">
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input type="checkbox" ref={isDefaultRef} />
              <span>Set as default</span>
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button className="btn btn-gold btn-sm" disabled={isSubmitting} onClick={async () => {
            const values = getFormValues();
            const errors = validateForm(values);
            if (Object.keys(errors).length > 0) {
              showToastMessage(Object.values(errors)[0], 'error');
              return;
            }
            setIsSubmitting(true);
            try {
              const result = await dispatch(createSenderId(values));
              if (result.payload?.success) {
                closeModal();
                await Promise.all([
                  dispatch(getSenderIds({ per_page: itemsPerPage, page: currentPage })),
                  dispatch(getDefaultSenderId()),
                ]);
                showToastMessage('Sender ID created successfully!', 'success');
              } else if (result.error) {
                showToastMessage(result.payload?.message || 'Failed to create sender ID', 'error');
              }
            } catch (err) {
              const errorMessage = err.response?.data?.message || err.message || 'Failed to create sender ID';
              showToastMessage(errorMessage, 'error');
            } finally {
              setIsSubmitting(false);
            }
          }}>
            {isSubmitting ? 'Creating...' : 'Create Sender ID'}
          </button>
        </div>
      </div>
    );
  };

  const handleEditSenderId = (senderId) => {
    editingIdRef.current = senderId.id;

    setTimeout(() => {
      if (nameRef.current) nameRef.current.value = senderId.name || '';
      if (descriptionRef.current) descriptionRef.current.value = senderId.description || '';
      if (typeRef.current) typeRef.current.value = senderId.type || 'alphanumeric';
      if (countryCodeRef.current) countryCodeRef.current.value = senderId.country_code || '';
      if (statusRef.current) statusRef.current.value = senderId.status || 'pending';
      if (isDefaultRef.current) isDefaultRef.current.checked = senderId.is_default || false;
    }, 0);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Pencil size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Edit Sender ID: {senderId.name}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Sender ID <span style={{ color: 'var(--red)' }}>*</span></label>
            <input className="form-input" ref={nameRef} maxLength="11" defaultValue={senderId.name || ''} />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-textarea" rows="2" ref={descriptionRef} defaultValue={senderId.description || ''} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Type</label>
              <select className="form-select" ref={typeRef} defaultValue={senderId.type || 'alphanumeric'}>
                <option value="alphanumeric">Alphanumeric</option>
                <option value="numeric">Numeric</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Country Code</label>
              <select className="form-select" ref={countryCodeRef} defaultValue={senderId.country_code || ''}>
                {COUNTRY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-select" ref={statusRef} defaultValue={senderId.status || 'pending'}>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input type="checkbox" ref={isDefaultRef} defaultChecked={senderId.is_default || false} />
              <span>Set as default</span>
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button className="btn btn-gold btn-sm" disabled={isSubmitting} onClick={async () => {
            const values = getFormValues();
            const errors = validateForm(values);
            if (Object.keys(errors).length > 0) {
              showToastMessage(Object.values(errors)[0], 'error');
              return;
            }
            setIsSubmitting(true);
            try {
              const result = await dispatch(updateSenderId({ id: senderId.id, data: values }));
              if (result.payload?.success) {
                closeModal();
                await Promise.all([
                  dispatch(getSenderIds({ per_page: itemsPerPage, page: currentPage })),
                  dispatch(getDefaultSenderId()),
                ]);
                showToastMessage('Sender ID updated successfully!', 'success');
              } else if (result.error) {
                showToastMessage(result.payload?.message || 'Failed to update sender ID', 'error');
              }
            } catch (err) {
              const errorMessage = err.response?.data?.message || err.message || 'Failed to update sender ID';
              showToastMessage(errorMessage, 'error');
            } finally {
              setIsSubmitting(false);
            }
          }}>
            {isSubmitting ? 'Saving...' : <><Save size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Save Changes</>}
          </button>
        </div>
      </div>
    );
  };

  const handleDeleteSenderId = (senderId) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Delete Sender ID</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to delete <strong>{senderId.name}</strong>?
          </p>
          <div style={{ padding: '12px', background: 'var(--red-bg)', borderRadius: '8px', fontSize: '13px', color: 'var(--red)' }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />
            This will permanently delete the sender ID and it can no longer be used.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button className="btn btn-danger btn-sm" disabled={isSubmitting} onClick={async () => {
            setIsSubmitting(true);
            try {
              const result = await dispatch(deleteSenderId(senderId.id));
              if (result.payload?.success) {
                closeModal();
                await Promise.all([
                  dispatch(getSenderIds({ per_page: itemsPerPage, page: currentPage })),
                  dispatch(getDefaultSenderId()),
                ]);
                showToastMessage('Sender ID deleted successfully.', 'error');
              } else if (result.error) {
                showToastMessage(result.payload?.message || 'Failed to delete sender ID', 'error');
              }
            } catch (err) {
              const errorMessage = err.response?.data?.message || err.message || 'Failed to delete sender ID';
              showToastMessage(errorMessage, 'error');
            } finally {
              setIsSubmitting(false);
            }
          }}>
            {isSubmitting ? 'Deleting...' : 'Delete Sender ID'}
          </button>
        </div>
      </div>
    );
  };

  // ==================== APPROVE WITH CONFIRMATION ====================
  const handleApprove = (senderId) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><CheckCircle2 size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Approve Sender ID</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to approve <strong>{senderId.name}</strong>?
          </p>
          <div style={{ padding: '12px', background: 'var(--green-bg)', borderRadius: '8px', fontSize: '13px', color: 'var(--green)' }}>
            <CheckCircle2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />
            This sender ID will become available for use in SMS sending.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button
            className="btn btn-gold btn-sm"
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                await dispatch(approveSenderId(senderId.id)).unwrap();
                closeModal();
                await dispatch(getSenderIds({ per_page: itemsPerPage, page: currentPage }));
                showToastMessage('Sender ID approved', 'success');
              } catch (err) {
                showToastMessage(err.message || 'Approval failed', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            Approve
          </button>
        </div>
      </div>
    );
  };

  // ==================== REJECT WITH CONFIRMATION ====================
  const handleReject = (senderId) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><XCircle size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Reject Sender ID</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to reject <strong>{senderId.name}</strong>?
          </p>
          <div style={{ padding: '12px', background: 'var(--red-bg)', borderRadius: '8px', fontSize: '13px', color: 'var(--red)' }}>
            <XCircle size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />
            This sender ID will not be usable and can be deleted later.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button
            className="btn btn-danger btn-sm"
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                await dispatch(rejectSenderId(senderId.id)).unwrap();
                closeModal();
                await dispatch(getSenderIds({ per_page: itemsPerPage, page: currentPage }));
                showToastMessage('Sender ID rejected', 'info');
              } catch (err) {
                showToastMessage(err.message || 'Rejection failed', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            Reject
          </button>
        </div>
      </div>
    );
  };

  // ==================== OTHER ACTION HANDLERS ====================

  const handleToggleStatus = async (id) => {
    setIsSubmitting(true);
    try {
      await dispatch(toggleSenderIdStatus(id)).unwrap();
      await dispatch(getSenderIds({ per_page: itemsPerPage, page: currentPage }));
      showToastMessage('Status toggled', 'success');
    } catch (err) {
      showToastMessage(err.message || 'Toggle failed', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSetDefault = async (id) => {
    setIsSubmitting(true);
    try {
      await dispatch(setDefaultSenderId(id)).unwrap();
      await Promise.all([
        dispatch(getSenderIds({ per_page: itemsPerPage, page: currentPage })),
        dispatch(getDefaultSenderId()),
      ]);
      showToastMessage('Default sender ID updated', 'success');
    } catch (err) {
      showToastMessage(err.message || 'Failed to set default', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ==================== RENDER FUNCTIONS ====================

  const renderGridView = () => {
    return (
      <div className="sender-ids-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
        {senderIds.map(item => {
          const color = item.status === 'pending' ? '#f59e0b' :
                        item.status === 'approved' || item.status === 'active' ? '#22c55e' :
                        item.status === 'rejected' ? '#ef4444' : '#94a3b8';
          return (
            <div key={item.id} className="card" style={{ borderTop: `3px solid ${color}` }}>
              {/* Header: icon, name, description, status badge, default badge, toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                <div style={{
                  width: '46px',
                  height: '46px',
                  borderRadius: '12px',
                  background: `${color}18`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '22px',
                  flexShrink: 0,
                }}>
                  <Send size={22} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--navy)' }}>{item.name}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>{item.description || 'No description'}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                  {item.is_default && (
                    <span className="badge badge-gold" style={{ background: 'var(--gold)', color: 'var(--navy)', fontSize: '10px' }}>
                      Default
                    </span>
                  )}
                  {getStatusBadge(item.status)}
                  {(item.status === 'approved' || item.status === 'active' || item.status === 'inactive') && (
                    <label className="toggle" title={isActive(item) ? 'Click to deactivate' : 'Click to activate'}>
                      <input
                        type="checkbox"
                        checked={isActive(item)}
                        onChange={(e) => handleToggleStatus(item.id)}
                        disabled={isSubmitting || isSubmittingRedux}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  )}
                </div>
              </div>

              {/* Stats row: Type, Country, Status, Default */}
              <div style={{ display: 'flex', gap: '16px', padding: '10px', background: 'var(--cream)', borderRadius: '8px', marginBottom: '12px' }}>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Type</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--navy)', marginTop: '2px' }}>{getTypeLabel(item.type)}</div>
                </div>
                <div style={{ flex: 1, borderLeft: '1px solid var(--border)', paddingLeft: '12px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Country</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--navy)', marginTop: '2px' }}>{item.country_code || '—'}</div>
                </div>
                <div style={{ flex: 1, borderLeft: '1px solid var(--border)', paddingLeft: '12px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Status</div>
                  <div style={{ marginTop: '2px' }}>{getStatusBadge(item.status)}</div>
                </div>
                <div style={{ flex: 1, borderLeft: '1px solid var(--border)', paddingLeft: '12px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Default</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--navy)', marginTop: '2px' }}>
                    {item.is_default ? <CheckCircle2 size={14} color="var(--green)" /> : <XCircle size={14} color="var(--red)" />}
                  </div>
                </div>
              </div>

              {/* Footer: date on left, actions on right */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: 'auto',
                paddingTop: '8px',
                borderTop: '1px solid var(--border-light)',
              }}>
                <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>
                  Created: {formatDate(item.created_at)}
                </div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {/* Approve – light green background */}
                  {item.status === 'pending' && (
                    <>
                      <button
                        className="btn btn-sm btn-icon btn-approve"
                        onClick={() => handleApprove(item)}
                        disabled={isSubmitting || isSubmittingRedux}
                        title="Approve"
                      >
                        <CheckCircle2 size={14} />
                      </button>
                      <button
                        className="btn btn-sm btn-icon btn-reject"
                        onClick={() => handleReject(item)}
                        disabled={isSubmitting || isSubmittingRedux}
                        title="Reject"
                      >
                        <XCircle size={14} />
                      </button>
                    </>
                  )}

                  {/* Set as default – light gold background */}
                  {isActive(item) && !item.is_default && (
                    <button
                      className="btn btn-sm btn-icon btn-default"
                      onClick={() => handleSetDefault(item.id)}
                      disabled={isSubmitting || isSubmittingRedux}
                      title="Set as default"
                    >
                      <Star size={14} />
                    </button>
                  )}

                  {/* Edit – light blue background */}
                  <button
                    className="btn btn-sm btn-icon btn-edit"
                    onClick={() => handleEditSenderId(item)}
                    disabled={loading}
                    title="Edit"
                  >
                    <Pencil size={14} />
                  </button>

                  {/* Delete – light red background */}
                  <button
                    className="btn btn-sm btn-icon btn-delete"
                    onClick={() => handleDeleteSenderId(item)}
                    disabled={loading}
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderListView = () => {
    return (
      <div className="card" style={{ marginBottom: '20px', padding: 0, overflow: 'hidden' }}>
        <div className="card-header" style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
          <div className="card-title"><ClipboardList size={16} style={{ verticalAlign: '-3px' }} /> Sender IDs</div>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Sender ID</th>
                <th>Description</th>
                <th>Type</th>
                <th>Country</th>
                <th>Status</th>
                <th>Default</th>
                <th style={{ minWidth: '140px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {senderIds.length > 0 ? (
                senderIds.map(item => (
                  <tr key={item.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '8px',
                          background: '#4f46e5',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white'
                        }}>
                          <Send size={14} />
                        </div>
                        <div>
                          <div style={{ fontWeight: '600', color: 'var(--navy)' }}>{item.name}</div>
                          {item.is_default && <span className="badge badge-gold" style={{ background: 'var(--gold)', color: 'var(--navy)', fontSize: '9px' }}>Default</span>}
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize: '13px', color: 'var(--text-mid)' }}>{item.description || '—'}</td>
                    <td style={{ fontSize: '13px' }}>{getTypeLabel(item.type)}</td>
                    <td style={{ fontSize: '13px', color: 'var(--text-mid)' }}>{item.country_code || '—'}</td>
                    <td>{getStatusBadge(item.status)}</td>
                    <td>{item.is_default ? <CheckCircle2 size={14} color="var(--green)" /> : <XCircle size={14} color="var(--red)" />}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {/* Toggle – icon button */}
                        {(item.status === 'approved' || item.status === 'active' || item.status === 'inactive') && (
                          isActive(item) ? (
                            <button
                              className="btn btn-sm btn-icon btn-toggle"
                              onClick={() => handleToggleStatus(item.id)}
                              disabled={isSubmitting || isSubmittingRedux}
                              title="Deactivate"
                            >
                              <ToggleRight size={14} />
                            </button>
                          ) : (
                            <button
                              className="btn btn-sm btn-icon btn-toggle"
                              onClick={() => handleToggleStatus(item.id)}
                              disabled={isSubmitting || isSubmittingRedux}
                              title="Activate"
                            >
                              <ToggleLeft size={14} />
                            </button>
                          )
                        )}

                        {/* Approve – light green */}
                        {item.status === 'pending' && (
                          <>
                            <button
                              className="btn btn-sm btn-icon btn-approve"
                              onClick={() => handleApprove(item)}
                              disabled={isSubmitting || isSubmittingRedux}
                              title="Approve"
                            >
                              <CheckCircle2 size={14} />
                            </button>
                            <button
                              className="btn btn-sm btn-icon btn-reject"
                              onClick={() => handleReject(item)}
                              disabled={isSubmitting || isSubmittingRedux}
                              title="Reject"
                            >
                              <XCircle size={14} />
                            </button>
                          </>
                        )}

                        {/* Set as default – light gold */}
                        {isActive(item) && !item.is_default && (
                          <button
                            className="btn btn-sm btn-icon btn-default"
                            onClick={() => handleSetDefault(item.id)}
                            disabled={isSubmitting || isSubmittingRedux}
                            title="Set as default"
                          >
                            <Star size={14} />
                          </button>
                        )}

                        {/* Edit – light blue */}
                        <button
                          className="btn btn-sm btn-icon btn-edit"
                          onClick={() => handleEditSenderId(item)}
                          disabled={loading}
                          title="Edit"
                        >
                          <Pencil size={14} />
                        </button>

                        {/* Delete – light red */}
                        <button
                          className="btn btn-sm btn-icon btn-delete"
                          onClick={() => handleDeleteSenderId(item)}
                          disabled={loading}
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7">
                    <div className="empty-state">
                      <div className="empty-icon"><Send size={40} strokeWidth={1.5} /></div>
                      <div className="empty-title">No sender IDs found</div>
                      <div className="empty-text">Create your first sender ID to get started.</div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '16px',
            padding: '14px 20px',
            borderTop: '1px solid var(--border)',
          }}>
            <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
              Showing <strong>{((currentPageNum - 1) * itemsPerPage) + 1}</strong> to <strong>{Math.min(currentPageNum * itemsPerPage, totalItems)}</strong> of <strong>{totalItems}</strong> sender IDs
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-light)' }}>Show:</label>
                <select
                  className="form-select"
                  style={{ width: 'auto', padding: '4px 8px', fontSize: '12px' }}
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  disabled={loading}
                >
                  <option value={3}>3</option>
                  <option value={6}>6</option>
                  <option value={9}>9</option>
                  <option value={12}>12</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: '4px' }}>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={goToPreviousPage}
                  disabled={currentPageNum === 1 || loading}
                  style={{
                    opacity: currentPageNum === 1 ? 0.5 : 1,
                    cursor: currentPageNum === 1 ? 'not-allowed' : 'pointer',
                  }}
                >
                  ← Prev
                </button>

                {getPageNumbers().map(page => (
                  <button
                    key={page}
                    className={`btn btn-sm ${page === currentPageNum ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => goToPage(page)}
                    disabled={loading}
                  >
                    {page}
                  </button>
                ))}

                <button
                  className="btn btn-ghost btn-sm"
                  onClick={goToNextPage}
                  disabled={currentPageNum === totalPages || loading}
                  style={{
                    opacity: currentPageNum === totalPages ? 0.5 : 1,
                    cursor: currentPageNum === totalPages ? 'not-allowed' : 'pointer',
                  }}
                >
                  Next →
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ==================== MAIN RENDER ====================

  const total = senderIds.length;
  const pending = senderIds.filter(s => s.status === 'pending').length;
  const approved = senderIds.filter(s => s.status === 'approved').length;
  const active = senderIds.filter(s => s.status === 'active').length;
  const rejected = senderIds.filter(s => s.status === 'rejected').length;
  const inactive = senderIds.filter(s => s.status === 'inactive').length;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', fontWeight: '700', color: 'var(--navy)' }}>
            <Send size={20} style={{ verticalAlign: '-4px' }} /> Sender ID Management
          </h2>
          <p style={{ fontSize: '14px', color: 'var(--text-mid)' }}>
            Manage sender IDs used for SMS sending. Only approved sender IDs can be used.
          </p>
        </div>
        <button className="btn btn-gold btn-sm" onClick={openCreateModal} disabled={isSubmitting}>
          + Add Sender ID
        </button>
      </div>

      {/* ===== STATS GRID ===== */}
      <div className="stats-grid" style={{ marginBottom: '20px' }}>
        <div className="stat-card gold">
          <div className="stat-label">Total Sender IDs</div>
          <div className="stat-value">{pagination.total || total}</div>
          <div className="stat-change up">↑ total</div>
          <div className="stat-icon"><Send size={20} /></div>
        </div>

        <div className="stat-card green">
          <div className="stat-label">Approved / Active</div>
          <div className="stat-value">{approved + active}</div>
          <div className="stat-change up">↑ ready</div>
          <div className="stat-icon"><CheckCircle2 size={20} /></div>
        </div>

        <div className="stat-card amber">
          <div className="stat-label">Pending</div>
          <div className="stat-value">{pending}</div>
          <div className="stat-change down">↑ needs review</div>
          <div className="stat-icon"><Clock size={20} /></div>
        </div>

        <div className="stat-card red">
          <div className="stat-label">Rejected / Inactive</div>
          <div className="stat-value">{rejected + inactive}</div>
          <div className="stat-change down">↓ inactive</div>
          <div className="stat-icon"><XCircle size={20} /></div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
          <div className="search-box" style={{ maxWidth: '300px', flex: '1' }}>
            <span className="search-icon"><Search size={14} /></span>
            <input
              type="text"
              placeholder="Search sender IDs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select className="form-select" style={{ width: '140px' }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="rejected">Rejected</option>
          </select>
          <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
            <button
              className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-ghost'} btn-sm`}
              onClick={() => setViewMode('grid')}
            >
              <LayoutGrid size={14} style={{ verticalAlign: '-2px' }} /> Grid
            </button>
            <button
              className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-ghost'} btn-sm`}
              onClick={() => setViewMode('list')}
            >
              <List size={14} style={{ verticalAlign: '-2px' }} /> List
            </button>
          </div>
        </div>
      </div>

      {loading && senderIds.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-light)' }} />
          <div style={{ color: 'var(--text-light)', marginTop: '8px' }}>Loading sender IDs...</div>
        </div>
      ) : (
        <>
          {senderIds.length > 0 ? (
            viewMode === 'grid' ? renderGridView() : renderListView()
          ) : (
            <div className="empty-state">
              <div className="empty-icon"><Send size={40} strokeWidth={1.5} /></div>
              <div className="empty-title">No sender IDs found</div>
              <div className="empty-text">Create your first sender ID to get started.</div>
            </div>
          )}
        </>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }
        .stat-card {
          position: relative;
          padding: 16px 20px;
          background: var(--white);
          border: 1px solid var(--border);
          border-radius: var(--radius);
          box-shadow: var(--shadow);
          transition: transform 0.2s, box-shadow 0.2s;
          cursor: default;
        }
        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-lg);
        }
        .stat-card .stat-label {
          font-size: 12px;
          color: var(--text-light);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-weight: 600;
        }
        .stat-card .stat-value {
          font-size: 28px;
          font-weight: 700;
          font-family: 'Playfair Display', serif;
          color: var(--navy);
          margin: 4px 0 2px;
        }
        .stat-card .stat-change {
          font-size: 12px;
          font-weight: 600;
        }
        .stat-card .stat-change.up {
          color: var(--green);
        }
        .stat-card .stat-change.down {
          color: var(--red);
        }
        .stat-card .stat-icon {
          position: absolute;
          top: 16px;
          right: 16px;
          opacity: 0.2;
          color: var(--navy);
        }
        .stat-card.gold .stat-icon { color: var(--gold); }
        .stat-card.green .stat-icon { color: var(--green); }
        .stat-card.amber .stat-icon { color: var(--amber); }
        .stat-card.red .stat-icon { color: var(--red); }

        .badge {
          padding: 3px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
          display: inline-block;
        }
        .badge-green {
          background: var(--green-bg);
          color: var(--green);
        }
        .badge-amber {
          background: var(--amber-bg);
          color: var(--amber);
        }
        .badge-red {
          background: var(--red-bg);
          color: var(--red);
        }
        .badge-navy {
          background: var(--navy);
          color: var(--gold-light);
        }
        .badge-gold {
          background: var(--gold);
          color: var(--navy);
        }

        .toggle {
          position: relative;
          display: inline-block;
          width: 40px;
          height: 22px;
          flex-shrink: 0;
        }
        .toggle input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: #ccc;
          transition: 0.3s;
          border-radius: 22px;
        }
        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 16px;
          width: 16px;
          left: 3px;
          bottom: 3px;
          background: white;
          transition: 0.3s;
          border-radius: 50%;
        }
        .toggle input:checked + .toggle-slider {
          background: var(--gold);
        }
        .toggle input:checked + .toggle-slider:before {
          transform: translateX(18px);
        }

        /* ----- Action button styles with light backgrounds ----- */
        .btn-approve {
          background: var(--green-bg) !important;
          color: var(--green) !important;
          border: 1px solid #a7d7be !important;
        }
        .btn-approve:hover {
          background: #22c55e !important;
          color: white !important;
          border-color: #22c55e !important;
        }

        .btn-reject {
          background: var(--red-bg) !important;
          color: var(--red) !important;
          border: 1px solid #fca5a5 !important;
        }
        .btn-reject:hover {
          background: #ef4444 !important;
          color: white !important;
          border-color: #ef4444 !important;
        }

        .btn-default {
          background: var(--gold-pale) !important;
          color: var(--gold) !important;
          border: 1px solid #fcd34d !important;
        }
        .btn-default:hover {
          background: var(--gold) !important;
          color: white !important;
          border-color: var(--gold) !important;
        }

        .btn-edit {
          background: var(--blue-bg) !important;
          color: var(--blue) !important;
          border: 1px solid #93c5fd !important;
        }
        .btn-edit:hover {
          background: var(--blue) !important;
          color: white !important;
          border-color: var(--blue) !important;
        }

        .btn-delete {
          background: var(--red-bg) !important;
          color: var(--red) !important;
          border: 1px solid #fca5a5 !important;
        }
        .btn-delete:hover {
          background: var(--red) !important;
          color: white !important;
          border-color: var(--red) !important;
        }

        .btn-toggle {
          background: var(--cream) !important;
          color: var(--text-mid) !important;
          border: 1px solid var(--border) !important;
        }
        .btn-toggle:hover {
          background: var(--border) !important;
          color: var(--text) !important;
        }

        .btn-sm {
          padding: 4px 10px !important;
          font-size: 12px !important;
        }
        .btn-icon {
          padding: 4px 6px !important;
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-gold {
          background: var(--gold) !important;
          color: var(--navy) !important;
          border: none !important;
        }
        .btn-gold:hover {
          background: var(--gold-dark) !important;
          color: var(--navy) !important;
        }

        .btn-danger {
          background: var(--red) !important;
          color: white !important;
          border: none !important;
        }
        .btn-danger:hover {
          background: var(--red-dark) !important;
          color: white !important;
        }

        .btn-ghost {
          background: transparent !important;
          color: var(--text) !important;
          border: 1px solid var(--border) !important;
        }
        .btn-ghost:hover {
          background: var(--cream) !important;
          color: var(--text) !important;
        }

        .btn-primary {
          background: var(--gold) !important;
          color: var(--navy) !important;
          border: none !important;
        }
        .btn-primary:hover {
          background: var(--gold-dark) !important;
          color: var(--navy) !important;
        }

        .empty-state {
          text-align: center;
          padding: 40px 0;
        }
        .empty-icon {
          font-size: 32px;
          margin-bottom: 12px;
          color: var(--text-light);
        }
        .empty-title {
          font-size: 16px;
          font-weight: 600;
          color: var(--text);
          margin-bottom: 4px;
        }
        .empty-text {
          font-size: 14px;
          color: var(--text-light);
        }

        .table-wrap {
          overflow-x: auto;
        }
        table {
          width: 100%;
          border-collapse: collapse;
        }
        th {
          text-align: left;
          padding: 10px 12px;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--text-light);
          border-bottom: 2px solid var(--border);
          background: var(--cream);
        }
        td {
          padding: 10px 12px;
          border-bottom: 1px solid var(--border-light);
          font-size: 13px;
        }
        tr:hover td {
          background: var(--cream);
        }

        @media (max-width: 1024px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .sender-ids-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: 1fr !important;
          }
          .sender-ids-grid {
            grid-template-columns: 1fr !important;
          }
          .card-header {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 10px;
          }
          .pagination-controls {
            flex-direction: column;
            align-items: center;
          }
          .page-numbers {
            flex-wrap: wrap;
            justify-content: center;
          }
        }
        @media (max-width: 480px) {
          .btn-sm {
            padding: 4px 8px;
            font-size: 11px;
          }
        }
      `}</style>
    </>
  );
};

export default SenderIds;